import {
  Button,
  DataTable,
  FlexBox,
  IconButton,
  Page,
  Timestamp,
} from '@dixa/lib-shared-habla';
import { EmailDraftT } from 'lib-shared-legacy-types';
import moment from 'moment-timezone';
import { useState } from 'react';

import type { EmailDraftV2 } from 'apis/userStorage';
import { appActions } from 'app/app.actions';
// eslint-disable-next-line no-restricted-imports
import { composeEmailActions } from 'components/shared/composeEmail/actions/composeEmailActions';
// eslint-disable-next-line no-restricted-imports
import DraftEmailHelper from 'components/shared/composeEmail/helper/DraftEmailHelper';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useServerStorage } from 'utils/useServerStorage/useServerStorage';

import { convertLocalDraftToV2 } from './drafts.utils';

export const DraftsView = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const { emailComposeV2 } = useFeatureFlag(['email-compose-v2']);
  const { emailDrafts, meta } = useAppSelector((state) => state.composeEmail);
  const { data: userStorage, set: setUserStorage } = useServerStorage('user');
  const [localDrafts, setLocalDrafts] = useState(
    () => DraftEmailHelper.getDrafts()?.drafts ?? [],
  );

  // V2: drafts from server storage
  const emailDraftsV2 = userStorage.drafts?.email ?? [];

  const handleImportLocalDrafts = () => {
    setUserStorage({
      drafts: (storage) => {
        const items = (storage.email ??= []);
        type DraftItem = (typeof items)[0];
        for (const draft of localDrafts) {
          items.push(convertLocalDraftToV2(draft) as unknown as DraftItem);
        }
      },
    });
    DraftEmailHelper.setDrafts(null);
    setLocalDrafts([]);
  };

  const handleEditV2 = (draft: EmailDraftV2) => {
    dispatch(
      appActions.showModal({
        modal: {
          type: 'app:composeEmail',
          arg: { draft },
        },
      }),
    );
  };

  const handleRemoveV2 = (draft: EmailDraftV2) => {
    setUserStorage({
      drafts: (storage) => {
        if (!storage.email) return;
        const idx = storage.email.findIndex(
          (d) => d.timestamp === draft.timestamp,
        );
        if (idx !== -1) storage.email.splice(idx, 1);
      },
    });
  };

  // V1: legacy localStorage drafts
  const handleEdit = (draft: EmailDraftT) => {
    dispatch(
      composeEmailActions.composeEmailOpenModal({
        initialValues: draft,
      }),
    );
  };

  const handleRemove = (draft: EmailDraftT) => {
    dispatch(
      composeEmailActions.composeEmailRemoveDraft({
        timestamp: draft.timestamp,
      }),
    );
  };

  if (emailComposeV2) {
    return (
      <Page>
        <Page.Head
          title="Email drafts"
          controls={
            localDrafts.length > 0 ? (
              <Button variant="secondary" onClick={handleImportLocalDrafts}>
                Import {localDrafts.length} old draft
                {localDrafts.length !== 1 ? 's' : ''} from this device
              </Button>
            ) : undefined
          }
        />

        <Page.Section>
          <DataTable
            data={emailDraftsV2 as EmailDraftV2[]}
            columns={[
              {
                label: 'From',
                selector: (draft) => {
                  return draft.fromEmail?.[0] ?? <em>No sender</em>;
                },
              },
              {
                label: 'To',
                selector: (draft) => {
                  return draft.toEmail[0]?.email ?? <em>No receiver</em>;
                },
              },
              {
                label: 'Subject',
                selector: (draft) => {
                  return draft.subject || <em>No subject</em>;
                },
              },
              {
                label: 'Message',
                selector: (draft) => {
                  return draft.body || <em>No message</em>;
                },
              },
              {
                label: 'Created',
                selector: (draft) => {
                  return (
                    <Timestamp
                      time={moment(draft.timestamp).toISOString()}
                      relative
                    />
                  );
                },
              },
              {
                label: '',
                selector: (draft) => {
                  return (
                    <FlexBox>
                      <IconButton
                        variant="tertiary"
                        icon="pen"
                        onClick={() => handleEditV2(draft)}
                      />

                      <IconButton
                        variant="tertiary"
                        icon="trash"
                        onClick={() => handleRemoveV2(draft)}
                      />
                    </FlexBox>
                  );
                },
                style: {
                  width: 0,
                },
              },
            ]}
            tableStatusProps={{
              emptyState:
                emailDraftsV2.length === 0
                  ? {
                      title: 'No email drafts',
                      description: "You haven't saved any email drafts yet.",
                    }
                  : undefined,
            }}
          />
        </Page.Section>
      </Page>
    );
  }

  return (
    <Page>
      <Page.Head title="Email drafts" />

      <Page.Section>
        <DataTable
          data={emailDrafts as EmailDraftT[]}
          columns={[
            {
              label: 'From',
              selector: (draft) => {
                return draft.fromEmails ?? <em>No sender</em>;
              },
            },
            {
              label: 'To',
              selector: (draft) => {
                return draft.toUser?.[0]?.user?.email ?? <em>No receiver</em>;
              },
            },
            {
              label: 'Subject',
              selector: (draft) => {
                return draft.subject ?? <em>No subject</em>;
              },
            },
            {
              label: 'Message',
              selector: (draft) => {
                return draft.message ?? <em>No message</em>;
              },
            },
            {
              label: 'Created',
              selector: (draft) => {
                return (
                  <Timestamp
                    time={moment(draft.timestamp ?? Date.now()).toISOString()}
                    relative
                  />
                );
              },
            },
            {
              label: '',
              selector: (draft) => {
                return (
                  <FlexBox>
                    <IconButton
                      variant="tertiary"
                      icon="pen"
                      onClick={() => handleEdit(draft)}
                    />

                    <IconButton
                      variant="tertiary"
                      icon="trash"
                      onClick={() => handleRemove(draft)}
                    />
                  </FlexBox>
                );
              },
              style: {
                width: 0,
              },
            },
          ]}
          tableStatusProps={{
            loading: meta.getDrafts === 'loading',
            emptyState:
              meta.getDrafts === 'succeeded' && !emailDrafts.length
                ? {
                    title: 'No email drafts',
                    description: "You haven't saved any email drafts yet.",
                  }
                : undefined,
          }}
        />
      </Page.Section>
    </Page>
  );
};
